import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Horizontal filter toolbar (PLAN §3.3) rendered above an `AdminDataGrid`.
 *
 * A layout shell — the caller composes `<SearchInput>`, `<StatusMultiSelect>`,
 * and `<DateRangeFilter>` as `children`. `onClear` renders a "Clear filters"
 * link (shown only when `canClear`, i.e. some filter is active) that resets
 * the page's URL filter state via `useUrlFilters().reset`.
 */
export interface FilterBarProps {
  children: ReactNode;
  /** Whether any filter is currently active (controls the Clear link). */
  canClear?: boolean;
  onClear?: () => void;
}

export const FilterBar = ({
  children,
  canClear = false,
  onClear,
}: FilterBarProps): ReactElement => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 1.5,
        mb: 2,
      }}
    >
      {children}
      {onClear ? (
        <Button
          variant="text"
          size="small"
          onClick={onClear}
          disabled={!canClear}
          sx={{ ml: 'auto' }}
        >
          {t('filters.clear')}
        </Button>
      ) : null}
    </Box>
  );
};
